'use client'

import { LoadingState } from '@repo/ui/LoadingState'
import { useCustomDataPageStore } from '@repo/store/useCustomDataPageStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useState, useEffect } from 'react'
import { ExtensionField } from '@repo/ui/ExtensionField'
import MUIAlert from '@mui/material/Alert'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import styles from '../MatterDrawerMatterTab/MatterDrawerMatterTab.module.scss'

export const MatterDrawerMatterExtensionsTab = ({ workspaceDetailCardVisible = false }) => {
  const { matters, currentMatter, matterDrawerIsOpen, matterDrawerClientMatterNo } = useMattersStore()
  const { fetchMatterExtensions } = useCustomDataPageStore()
  const { clientNo, clientMatterNo } = currentMatter || {}
  const [matter, setMatter] = useState(null)
  const [isFetchingData, setIsFetchingData] = useState(false)
  const [matterExtensions, setMatterExtensions] = useState(null)

  useEffect(() => {
    if (matterDrawerIsOpen && matterDrawerClientMatterNo) {
      const matter = matters[matterDrawerClientMatterNo]
      setMatter(matter)
    }

    if (!matterDrawerClientMatterNo && !matterDrawerIsOpen && workspaceDetailCardVisible && clientMatterNo) {
      setMatter(currentMatter)
    }
  }, [
    matterDrawerClientMatterNo,
    matterDrawerIsOpen,
    workspaceDetailCardVisible,
    clientMatterNo,
    matters,
    currentMatter
  ])

  useEffect(() => {
    const getMatterExtensions = async ({ clientMatterNo }) => {
      setIsFetchingData(true)
      setMatterExtensions(null)
      const workTypeId = matter?.workflowTypeId || null

      if (!workTypeId || !clientMatterNo) {
        setIsFetchingData(false)
        return
      }

      const extensions = await fetchMatterExtensions({ clientMatterNo, workTypeId, includeValues: true })
      setMatterExtensions(extensions || null)
      setIsFetchingData(false)
    }

    if (matterDrawerIsOpen && matterDrawerClientMatterNo) {
      getMatterExtensions({ clientMatterNo: matterDrawerClientMatterNo })
    }

    if (!matterDrawerClientMatterNo && !matterDrawerIsOpen && workspaceDetailCardVisible && clientMatterNo) {
      getMatterExtensions({ clientMatterNo: clientMatterNo })
    }
  }, [
    workspaceDetailCardVisible,
    clientMatterNo,
    clientNo,
    matterDrawerClientMatterNo,
    matterDrawerIsOpen,
    matter,
    fetchMatterExtensions
  ])

  if (
    isFetchingData ||
    (!workspaceDetailCardVisible && !matterDrawerClientMatterNo) ||
    (workspaceDetailCardVisible && !clientMatterNo) ||
    !matter
  ) {
    return (
      <MUIStack
        justifyContent="center"
        alignItems="center"
        sx={{ width: '100%', height: '300px' }}
        data-testid="MatterDrawerMatterExtensionsTab"
      >
        <LoadingState />
      </MUIStack>
    )
  }

  const isUserRestricted = matter?.isUserRestricted || false

  if (isUserRestricted) {
    return (
      <MUIStack
        justifyContent="flex-start"
        alignItems="center"
        sx={{ width: '100%', height: '300px' }}
        data-testid="MatterDrawerMatterExtensionsTab"
      >
        <MUIAlert severity="error" sx={{ m: '30px 16px 16px' }}>
          You are not authorised to view details about this matter
        </MUIAlert>
      </MUIStack>
    )
  }

  if (!matterExtensions && !isFetchingData) {
    return (
      <MUIStack
        justifyContent="flex-start"
        alignItems="center"
        sx={{ width: '100%', height: '300px' }}
        data-testid="MatterDrawerMatterExtensionsTab"
      >
        <MUIAlert severity="info" sx={{ m: '30px 16px 16px' }}>
          No matter extensions found for this matter
        </MUIAlert>
      </MUIStack>
    )
  }

  return (
    <MUIStack
      data-testid="MatterDrawerMatterExtensionsTab"
      role="tabpanel"
      className={styles.MatterDrawerMatterTab}
      sx={{ padding: '24px', width: '100%' }}
    >
      <MUIStack
        className={styles.MatterDrawerMatterTab}
        direction="row"
        justifyContent="space-between"
        sx={{ width: '100%', position: 'relative' }}
        flexWrap="wrap"
        gap={8}
      >
        {matterExtensions?.map((extension) => (
          <MUIStack gap={4} key={extension.questionnaireId} sx={{ width: '100%' }}>
            <MUITypography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                mb: 0,
                ml: 3,
                mt: 3
              }}
            >
              {extension?.description}
            </MUITypography>

            {extension?.fields?.map((field, index) => {
              return (
                <ExtensionField
                  width={'100%'}
                  field={field}
                  index={index}
                  extension={extension}
                  key={`${field.fieldId}-${field.pageId}-${index}`}
                />
              )
            })}
          </MUIStack>
        ))}
      </MUIStack>
    </MUIStack>
  )
}

MatterDrawerMatterExtensionsTab.propTypes = {
  workspaceDetailCardVisible: PropTypes.bool
}
